/** @file Sign-in, sign-up, and confirmation handlers for the Amplify Authenticator */

import {
  confirmSignUp,
  type ConfirmSignUpInput,
  signIn,
  type SignInInput,
  signOut,
  signUp,
  type SignUpInput,
} from 'aws-amplify/auth';

import { readAdminInfo, writeAdminInfo } from '@/auth';
import { getUserInfo } from '@/pages/AccountSettings/api';

/** DOM id of the extra email field shown on the support-staff admin login form. */
export const ADMIN_EMAIL_INPUT_ID = 'admin-email';

/**
 * sessionStorage key for the message shown on the admin login form after a failed
 * admin login. It lives outside the Authenticator because signing the admin back out
 * resets the form, which discards any error the form was holding.
 */
export const ADMIN_LOGIN_ERROR_STORAGE_KEY = 'adminLoginError';

export const readAdminLoginError = (): string =>
  window.sessionStorage.getItem(ADMIN_LOGIN_ERROR_STORAGE_KEY) || '';

export const clearAdminLoginError = (): void =>
  window.sessionStorage.removeItem(ADMIN_LOGIN_ERROR_STORAGE_KEY);

export const adminLoginErrorMessage = (adminEmail: string): string =>
  `Could not load a customer account for "${adminEmail}". Check the address and try again.`;

interface AuthServiceDeps {
  setUser: (id: string, properties: Record<string, unknown>) => void;
  trackEvent: (name: any, properties?: Record<string, unknown>) => Promise<void> | void;
}

/**
 * Build the `services` object for the Amplify Authenticator.
 *
 * Admin login is a support-staff sign-in with their own Cognito credentials plus the
 * email of the customer to act as. The backend matches that email exactly on every
 * later request, so after sign-in the typed address is swapped for the customer's
 * stored address. If no customer matches, the admin is signed back out so they land
 * on this form with a message instead of inside an app that can never load.
 *
 * @param {AuthServiceDeps} deps - Analytics hooks
 * @returns {object} - Authenticator services
 */
export const createAuthServices = ({ setUser, trackEvent }: AuthServiceDeps) => ({
  async handleSignIn(formData: SignInInput) {
    const { username, password } = formData;
    clearAdminLoginError();
    const adminInfo = readAdminInfo();
    const isAdminLogin = adminInfo.isAdminLogin === true;
    const adminInputField = document.getElementById(
      ADMIN_EMAIL_INPUT_ID,
    ) as HTMLInputElement | null;
    const adminEmail = adminInputField ? adminInputField.value.trim() : '';

    const res = await signIn({
      username,
      password,
      options: {
        authFlowType: 'USER_PASSWORD_AUTH',
      },
    });
    if (isAdminLogin) {
      writeAdminInfo({ ...adminInfo, adminEmail });
    }
    if (!res.isSignedIn) {
      return res;
    }

    let userInfo;
    try {
      userInfo = await getUserInfo();
    } catch (err) {
      if (!isAdminLogin) throw err;
      writeAdminInfo({ ...adminInfo, adminEmail: '' });
      window.sessionStorage.setItem(
        ADMIN_LOGIN_ERROR_STORAGE_KEY,
        adminLoginErrorMessage(adminEmail),
      );
      try {
        await signOut();
      } catch (signOutErr) {
        console.error('Error during sign out:', signOutErr);
      }
      throw new Error(adminLoginErrorMessage(adminEmail));
    }

    if (isAdminLogin && userInfo.user.email) {
      writeAdminInfo({ ...adminInfo, adminEmail: userInfo.user.email });
    }
    setUser(userInfo.user.id, {
      $email: userInfo.user.email,
      $name: userInfo.user.email,
      organization_name: userInfo.user.organization_name,
    });
    await trackEvent('User Login', { id: userInfo.user.id });
    return res;
  },

  async handleSignUp(formData: SignUpInput) {
    const { username, password, options } = formData;
    const signUpResult = await signUp({
      username,
      password,
      options,
    });
    if (signUpResult.userId) {
      setUser(signUpResult.userId, { $email: username, $name: username });
    }
    await trackEvent('User Created', { id: signUpResult.userId });
    return signUpResult;
  },

  async handleConfirmSignUp(formdata: ConfirmSignUpInput) {
    const { username, confirmationCode } = formdata;
    try {
      const confirmSignUpResult = await confirmSignUp({
        username,
        confirmationCode,
      });
      await trackEvent('User Confirmed');
      return confirmSignUpResult;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
});
